import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { CheckCircle, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface EmailConfirmationModalProps {
  isOpen: boolean;
  email: string;
  onClose: () => void;
  onResendEmail: (email: string) => Promise<void>;
  onChangeEmail: () => void;
}

const EmailConfirmationModal: React.FC<EmailConfirmationModalProps> = ({
  isOpen,
  email,
  onClose,
  onResendEmail,
  onChangeEmail,
}) => {
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const maxResends = 3;
  const cooldownTime = 60; // 60 seconds
  
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Debug props changes
  useEffect(() => {
    console.log('📱 EmailConfirmationModal props changed:', {
      isOpen,
      email,
      timestamp: new Date().toISOString()
    });
  }, [isOpen, email]);

  // Handle cooldown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendCooldown]);

  // Accessibility announcement and focus management
  useEffect(() => {
    if (isOpen) {
      console.log('🎯 Modal opening - setting up accessibility and focus for:', email);
      
      // Announce to screen readers
      const announcement = `Verification email sent to ${email}`;
      const ariaLive = document.createElement('div');
      ariaLive.setAttribute('role', 'status');
      ariaLive.setAttribute('aria-live', 'polite');
      ariaLive.className = 'sr-only';
      ariaLive.textContent = announcement;
      document.body.appendChild(ariaLive);

      // Focus management
      setTimeout(() => {
        if (closeButtonRef.current) {
          console.log('🎯 Setting focus on close button');
          closeButtonRef.current.focus();
        } else {
          console.warn('⚠️ Close button ref not available for focus');
        }
      }, 100);

      return () => {
        console.log('🧹 Cleaning up modal accessibility elements');
        if (document.body.contains(ariaLive)) {
          document.body.removeChild(ariaLive);
        }
      };
    } else {
      console.log('❌ Modal not open, skipping accessibility setup');
    }
  }, [isOpen, email]);

  const handleResend = async () => {
    console.log('📤 Resend email attempt:', { resendCount, maxResends, resendCooldown });
    
    if (resendCount >= maxResends) {
      console.warn('⚠️ Maximum resend attempts reached');
      toast.error('Maximum resend attempts reached. Please contact support if you continue having issues.');
      return;
    }

    if (resendCooldown > 0) {
      console.warn('⚠️ Resend attempted during cooldown period');
      return;
    }

    try {
      console.log('📧 Starting email resend process');
      setIsResending(true);
      await onResendEmail(email);
      setResendCount((prev) => prev + 1);
      setResendCooldown(cooldownTime);
      console.log('✅ Email resend successful');
      toast.success('Verification email resent! Please check your inbox.');
    } catch (error) {
      console.error('❌ Failed to resend email:', error);
      toast.error('Failed to resend verification email. Please try again later.');
    } finally {
      setIsResending(false);
    }
  };

  const getResendButtonText = () => {
    if (isResending) return 'Sending...';
    if (resendCooldown > 0) return `Resend in ${resendCooldown}s`;
    if (resendCount >= maxResends) return 'Max attempts reached';
    return 'Resend verification email';
  };

  const isResendDisabled = resendCooldown > 0 || isResending || resendCount >= maxResends;

  // Debug render
  console.log('🎨 EmailConfirmationModal rendering:', { isOpen, email, hasEmail: !!email });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-md"
        aria-labelledby="email-confirmation-title"
        aria-describedby="email-confirmation-description"
      >
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 text-green-600">
            <CheckCircle className="h-full w-full" />
          </div>
          <DialogTitle id="email-confirmation-title" className="text-xl font-semibold">
            Account Created Successfully!
          </DialogTitle>
          <DialogDescription 
            id="email-confirmation-description" 
            className="text-center mt-4"
          >
            We've sent a verification email to:
          </DialogDescription>
        </DialogHeader>
        
        <div className="text-center mb-4">
          <div className="font-semibold text-foreground break-all bg-muted/50 p-3 rounded-md">
            {email}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Please check your inbox and click the verification link. This may take up to 5 minutes to arrive.
          </p>
        </div>
        
        <div className="space-y-4">
          <Button
            onClick={handleResend}
            disabled={isResendDisabled}
            variant="outline"
            className="w-full"
          >
            {isResending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            {getResendButtonText()}
          </Button>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="help" className="border-none">
              <AccordionTrigger className="hover:no-underline text-sm">
                Didn't receive the email?
              </AccordionTrigger>
              <AccordionContent className="space-y-3 text-sm pt-2">
                <div className="space-y-2 text-muted-foreground">
                  <p>• Check your spam or junk folder</p>
                  <p>• Make sure {email} is correct</p>
                  <p>• Add noreply@lightshunt.com to your contacts</p>
                  <p>• Wait a few minutes - emails can be delayed</p>
                </div>
                <Button
                  variant="link"
                  onClick={onChangeEmail}
                  className="p-0 h-auto text-sm"
                  size="sm"
                >
                  Update email address
                </Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Button 
            ref={closeButtonRef}
            onClick={onClose} 
            className="w-full"
          >
            OK, I'll check my email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailConfirmationModal;